import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useAuth } from "providers/AuthProvider";
import DocumentList from "components/MyFileTabs/DocumentList";
import RaiDialog from "components/MyFileTabs/RaiDialog";
import AhDialog from "components/MyFileTabs/AhDialog";
import FileUploader from "components/MyFileTabs/FileUploader";
import MultiFileUploader from "components/MyFileTabs/MultiFileUploader";
import MultiFileUploaderMobile from "components/MyFileTabs/MultiFileUploaderMobile";
import { useMenu } from "hooks/useMenuFooter";
import { useDocuments } from "hooks/useDocuments";

function Documents({ id, data, statut, fetchDossierData }) {
  const { data: filesResponse, mutate: fetchFilesData } = useDocuments(id);
  const filesData = useMemo(
    () => filesResponse?.data ?? [],
    [filesResponse?.data],
  );
  const { isMobile } = useMenu();
  const { role, isManager, isPartner, isCustomer } = useAuth();
  const [raiDialogOpen, setRaiDialogOpen] = useState(false);
  const [ahDialogOpen, setAhDialogOpen] = useState(false);
  const dossierClientId = data.data?.attributes?.client?.data?.id;

  // Documents du client : documents fournis par le bénéficiaire
  const clientFilesData = useMemo(
    () =>
      filesData.filter(
        (file) => +file.attributes?.proprietaire?.data?.id === +dossierClientId,
      ),
    [dossierClientId, filesData],
  );

  // Documents des partenaires : documents avec le channel "partner"
  const partnerFilesData = useMemo(
    () =>
      filesData.filter(({ attributes: { channel } }) => channel === "partner"),
    [filesData],
  );

  // Documents des gestionnaires : tous les autres
  const managerFilesData = useMemo(() => {
    const excludedIds = [...clientFilesData, ...partnerFilesData].map(
      ({ id }) => id,
    );
    return filesData.filter(({ id }) => !excludedIds.includes(+id));
  }, [clientFilesData, filesData, partnerFilesData]);

  const openModal = (type) => {
    if (type === "RAI") {
      setRaiDialogOpen(true);
    } else if (type === "AH") {
      setAhDialogOpen(true);
    }
  };

  const closeModal = (type) => {
    if (type === "RAI") {
      setRaiDialogOpen(false);
    } else if (type === "AH") {
      setAhDialogOpen(false);
    }
  };

  const showAHButton = [
    "RAI envoyé",
    "Travaux en cours",
    "Travaux terminés",
    "Documents reçus et à traiter",
  ].includes(statut);

  const showRAIButton = statut === "Devis validé";

  return (
    <div>
      {raiDialogOpen && (
        <RaiDialog
          onClose={() => {
            closeModal("RAI");
          }}
          isOpen={raiDialogOpen}
          id={id}
          fetchFilesData={fetchFilesData}
          data={data}
        />
      )}

      {ahDialogOpen && (
        <AhDialog
          onClose={() => {
            closeModal("AH");
          }}
          isOpen={ahDialogOpen}
          id={id}
          fetchFilesData={fetchFilesData}
          data={data}
        />
      )}

      {(isManager || isPartner) && (
        <FileUploader
          id={id}
          channel={isPartner ? "partner" : "client"}
          fetchFilesData={fetchFilesData}
        />
      )}
      {isCustomer &&
        (isMobile ? (
          <MultiFileUploaderMobile
            dossier={data.data}
            files={clientFilesData}
            fetchFilesData={fetchFilesData}
          />
        ) : (
          <MultiFileUploader
            dossier={data.data}
            files={clientFilesData}
            fetchFilesData={fetchFilesData}
          />
        ))}

      {isManager &&
        role !== "Consultation" &&
        (showRAIButton || showAHButton) && (
          <div className="w-full mt-4 flex flex-row-reverse">
            {showRAIButton && (
              <button
                onClick={() => {
                  openModal("RAI");
                }}
                className="inline-flex items-center px-3 py-2 my-3 font-semibold leading-6 text-sm shadow rounded-md text-white bg-main-color transition ease-in-out duration-150"
              >
                Générer le RAI
              </button>
            )}
            {showAHButton && (
              <button
                onClick={() => {
                  openModal("AH");
                }}
                className="inline-flex items-center px-3 py-2 my-3 font-semibold leading-6 text-sm shadow rounded-md text-white bg-main-color transition ease-in-out duration-150"
              >
                Générer l&apos;attestation sur l&apos;honneur
              </button>
            )}
          </div>
        )}

      {(isCustomer || isPartner) && (
        <div className="mt-4">
          <DocumentList
            title="Mes documents"
            withState={isCustomer}
            dossier={data.data}
            documents={isCustomer ? clientFilesData : partnerFilesData}
            fetchDossierData={fetchDossierData}
            fetchFilesData={fetchFilesData}
            id={id}
          />
        </div>
      )}

      {isManager && (
        <div className="mt-4">
          <DocumentList
            title="Fichiers attachés par le bénéficiaire"
            dossier={data.data}
            documents={clientFilesData}
            fetchDossierData={fetchDossierData}
            fetchFilesData={fetchFilesData}
            id={id}
          />
        </div>
      )}

      {isManager && (
        <div className="mt-4">
          <DocumentList
            title="Fichiers échangés avec le partenaire"
            withState={false}
            dossier={data.data}
            documents={partnerFilesData}
            fetchDossierData={fetchDossierData}
            fetchFilesData={fetchFilesData}
            id={id}
          />
        </div>
      )}

      {(isCustomer || isManager || isPartner) && (
        <div className="mt-8">
          <DocumentList
            title="Les documents Sorégies"
            sendButton
            withState={false}
            dossier={data.data}
            documents={managerFilesData}
            fetchDossierData={fetchDossierData}
            fetchFilesData={fetchFilesData}
            id={id}
          />
        </div>
      )}
    </div>
  );
}
Documents.propTypes = {
  id: PropTypes.number.isRequired,
  data: PropTypes.instanceOf(Object).isRequired,
  statut: PropTypes.string.isRequired,
  fetchDossierData: PropTypes.func.isRequired,
};

export default Documents;
